// Shared field-display resolvers used by both the bot's Discord modal
// builder and the portal's preview component. The two surfaces have to
// render the same options, labels and ordering for a given field;
// putting the resolution logic here means a config drift (e.g. a
// trueLabel default rename or NPS endpoint copy change) shows up in
// both places automatically.
//
// These functions are pure: no React, no Discord builders. Each
// consumer maps the resolved data into its own primitives.

import type { RatingDisplay } from './field-schema.js';
import {
  BINARY_FALSE_LABEL_DEFAULT,
  BINARY_TRUE_LABEL_DEFAULT,
} from './field-schema.js';

// Resolves a binary field's labels with the standard Yes / No fallback.
// Stored answer is the canonical 'true' / 'false' regardless of what
// the labels say, so a later rename does not retroactively rewrite
// history.
export function resolveBinaryLabels(
  config: Record<string, unknown>,
): { trueLabel: string; falseLabel: string } {
  const trueRaw = config['trueLabel'];
  const falseRaw = config['falseLabel'];
  return {
    trueLabel:
      typeof trueRaw === 'string' && trueRaw.length > 0
        ? trueRaw
        : BINARY_TRUE_LABEL_DEFAULT,
    falseLabel:
      typeof falseRaw === 'string' && falseRaw.length > 0
        ? falseRaw
        : BINARY_FALSE_LABEL_DEFAULT,
  };
}

// Reads the rating display config off a field. Returns undefined for
// the default "numbers only" rendering; an explicit emoji config when
// the studio configured a decoration. The stored value is always the
// integer 1-5 regardless of display mode.
export function resolveRatingDisplay(
  config: Record<string, unknown>,
): RatingDisplay | undefined {
  const raw = config['display'];
  if (raw === null || typeof raw !== 'object') return undefined;
  const obj = raw as Record<string, unknown>;
  if (
    obj['mode'] === 'emoji' &&
    typeof obj['emoji'] === 'string' &&
    obj['emoji'].length > 0
  ) {
    return { mode: 'emoji', emoji: obj['emoji'] };
  }
  if (obj['mode'] === 'numbers') return { mode: 'numbers' };
  return undefined;
}

// Reads a select field's options array (filtered to non-empty strings)
// and resolves its maxValues, clamped to the option count and Discord's
// 25-item cap.
export function resolveSelectConfig(
  config: Record<string, unknown>,
): { options: string[]; maxValues: number } {
  const options = readStringArray(config['options']);
  const maxValues = readMaxValues(config['maxValues'], options.length);
  return { options, maxValues };
}

function readStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

function readMaxValues(raw: unknown, optionCount: number): number {
  if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 1) return 1;
  return Math.min(raw, optionCount, 25);
}

// NPS is a fixed 10 → 1 scale with explanatory suffixes on the
// endpoints. Both surfaces iterate this list verbatim so the ordering
// and endpoint copy can't drift.
export const NPS_OPTIONS: ReadonlyArray<{ value: number; label: string }> =
  Array.from({ length: 10 }, (_, i) => 10 - i).map((n) => ({
    value: n,
    label:
      n === 10
        ? '10 - Very likely'
        : n === 1
          ? '1 - Very unlikely'
          : String(n),
  }));

// Order of the rating radio: 5 → 1 so the highest score sits at the
// top, matching how studios talk about ratings ("would you give it a
// 5?"). Stored values stay integers 1-5.
export const RATING_VALUES: ReadonlyArray<number> = [5, 4, 3, 2, 1];

// Allowed severity levels in canonical display order.
export const SEVERITY_LEVELS: ReadonlyArray<string> = [
  'Low',
  'Medium',
  'High',
  'Critical',
];
